import React, { useCallback, useEffect, useMemo, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import ProgressReportPanel from "@/components/ProgressReportPanel";
import {
  School, Users, GraduationCap, Target, Percent, Trophy, AlertTriangle, BookOpenCheck, UserCheck, CheckCircle2, Clock3,
  ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

/**
 * The shared body of School Progress, for a given schoolId. Used by AdminSchoolAnalytics.js (admin, with a school picker in front
 * of this) and PrincipalProgress.js (a principal's own school).
 *
 * Reading order, each number shown once:
 *   1. the school in one line (students / teachers / classes)
 *   2. a plain-language verdict (how is the school doing?)
 *   3. the five numbers that matter, then where every student stands
 *   4. tabs for the detail: classes & teachers, students who need help, activity over time, the downloadable report
 * The verdict comes from the server (the reports open with the same sentence).
 */

const TEAL = "#5E9A93";
const CORAL = "#D2806D";
const GOLD = "#E2C27E";
const SLATE = "#5B6882";
const BORDER = "#E5DFC8";

const STATUS_META = {
  on_track: { label: "Up to date", hint: "has finished every story unlocked so far", color: TEAL },
  slightly_behind: { label: "Slightly behind", hint: "1 story still to finish", color: GOLD },
  behind: { label: "Behind", hint: "2 or more stories still to finish", color: CORAL },
  not_started: { label: "Not started", hint: "nothing is unlocked for the class yet", color: SLATE },
};

const VERDICT_STYLE = {
  great: "bg-[#E8F4F1] border-[#9CCBC3]",
  good: "bg-[#F1F6EA] border-[#C9DDA8]",
  attention: "bg-[#FFF3E6] border-[#F1C894]",
  waiting: "bg-[#FBF8F0] border-[#E5DFC8]",
  empty: "bg-[#FBF8F0] border-[#E5DFC8]",
};

const show = (v) => (v === null || v === undefined ? "—" : v);
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function Tile({ icon: Icon, value, label, hint, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5DFC8] p-5 shadow-sm" data-testid={testid}>
      <div className="rounded-xl bg-hv-tealTint p-2 inline-flex">
        <Icon className="w-5 h-5 text-hv-teal" strokeWidth={2.4} />
      </div>
      <p className="hv-heading text-3xl mt-3">{value}</p>
      <p className="text-sm text-hv-slate mt-1">{label}</p>
      {hint && <p className="text-xs text-hv-slate/80 mt-0.5">{hint}</p>}
    </div>
  );
}

function DeltaBadge({ delta }) {
  if (!delta) return <span className="h-4 block" />;
  const { direction, delta: d, value } = delta;
  if (value === null || value === undefined) return <span className="text-xs text-hv-slate">No data yet</span>;
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const color = direction === "up" ? "text-hv-teal" : direction === "down" ? "text-hv-coral" : "text-hv-slate";
  const sign = direction === "up" ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${color}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.8} />
      {d === null ? "—" : `${sign}${d}`} <span className="font-normal text-hv-slate">vs previous</span>
    </span>
  );
}

function PeriodMetricTile({ icon: Icon, label, delta, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5DFC8] p-5 shadow-sm" data-testid={testid}>
      <div className="rounded-xl bg-hv-tealTint p-2 inline-flex">
        <Icon className="w-5 h-5 text-hv-teal" strokeWidth={2.4} />
      </div>
      <p className="hv-heading text-3xl mt-3">{delta?.value ?? "—"}</p>
      <p className="text-sm text-hv-slate mt-1">{label}</p>
      <div className="mt-2"><DeltaBadge delta={delta} /></div>
    </div>
  );
}

function Verdict({ verdict }) {
  const Icon = verdict.level === "attention" ? AlertTriangle : verdict.level === "waiting" || verdict.level === "empty" ? Clock3 : CheckCircle2;
  return (
    <div className={`rounded-2xl border px-5 py-4 flex items-start gap-3 ${VERDICT_STYLE[verdict.level] || VERDICT_STYLE.waiting}`} data-testid="school-verdict" data-level={verdict.level}>
      <Icon className="w-6 h-6 shrink-0 mt-0.5 text-hv-navy" strokeWidth={2.4} />
      <div>
        <p className="hv-heading text-lg text-hv-navy leading-tight">{verdict.headline}</p>
        <p className="text-sm text-hv-slate mt-1">{verdict.detail}</p>
        {verdict.participation && verdict.pace && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="school-verdict-parts">
            <span className="rounded-full bg-white/70 border border-[#E5DFC8] px-3 py-1 text-xs text-hv-navy" title="Students who have opened at least one story">
              <b>Taking part:</b> {verdict.participation.value} of {verdict.participation.of} students ({verdict.participation.pct}%)
            </span>
            <span className="rounded-full bg-white/70 border border-[#E5DFC8] px-3 py-1 text-xs text-hv-navy" title="Of the students following along, how many have finished every story unlocked so far">
              <b>Keeping up:</b> {verdict.pace.value} of {verdict.pace.of} who started ({verdict.pace.pct}%)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBar({ status }) {
  const total = status.reduce((s, r) => s + r.count, 0);
  if (!total) return null;
  return (
    <div className="mt-4 rounded-2xl bg-white border border-[#E5DFC8] p-5" data-testid="status-breakdown">
      <p className="text-xs font-bold uppercase tracking-wider text-hv-slate mb-3">Where every student stands</p>
      <div className="h-4 rounded-full overflow-hidden flex w-full border border-[#E5DFC8]">
        {status.map((r) => (
          r.count > 0 && (
            <div key={r.key} style={{ width: `${(100 * r.count) / total}%`, background: STATUS_META[r.key].color }}
                 title={`${STATUS_META[r.key].label}: ${r.count}`} />
          )
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {status.map((r) => (
          <div key={r.key} className="flex items-start gap-2" data-testid={`status-${r.key}`}>
            <span className="w-3 h-3 rounded-sm mt-1 shrink-0" style={{ background: STATUS_META[r.key].color }} />
            <div>
              <p className="text-sm text-hv-navy"><span className="font-bold">{r.count}</span> {STATUS_META[r.key].label}</p>
              <p className="text-xs text-hv-slate">{STATUS_META[r.key].hint}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bar100({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-hv-slate">—</span>;
  return (
    <div className="flex items-center gap-2 min-w-[130px]">
      <div className="flex-1 h-2 rounded-full bg-[#EFE6D2] overflow-hidden"><div className="h-full rounded-full bg-hv-teal" style={{ width: `${Math.max(value, value > 0 ? 4 : 0)}%` }} /></div>
      <span className="w-12 text-right text-xs font-bold text-hv-navy">{value}%</span>
    </div>
  );
}

function WhereToLook({ data, onOpen }) {
  if (!data) return null;
  if (!data.items.length) {
    return (
      <div className="rounded-2xl bg-white border border-[#E5DFC8] px-5 py-4 flex items-center gap-3" data-testid="school-where-to-look">
        <CheckCircle2 className="w-6 h-6 text-hv-teal shrink-0" strokeWidth={2.2} />
        <p className="hv-heading text-hv-navy">Nothing needs attention right now. Every class is moving.</p>
      </div>
    );
  }
  return (
    <section data-testid="school-where-to-look">
      <h3 className="hv-heading text-lg text-hv-navy">Where to look first</h3>
      <p className="text-sm text-hv-slate mb-2">The classes that need a conversation, most urgent first.</p>
      <ul className="rounded-2xl bg-white border border-[#E5DFC8] divide-y divide-[#E5DFC8]">
        {data.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 px-5 py-3.5" data-testid="school-attention-item">
            <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${it.severity >= 2 ? "bg-[#E08A00]" : "bg-[#9AA79D]"}`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-hv-navy">{it.title}</p>
              <p className="text-sm text-hv-slate">{it.detail}</p>
            </div>
            {it.key && (
              <button type="button" className="shrink-0 text-xs font-bold text-hv-navy border-2 border-[#E5DFC8] rounded-full px-3 py-1 hover:bg-hv-cream"
                      onClick={() => onOpen(it.title.split(":")[0].replace(/ is falling behind$/, ""))}>Open class</button>
            )}
          </li>
        ))}
      </ul>
      {data.more > 0 && <p className="mt-2 text-xs text-hv-slate">…and {data.more} more. See the class table below.</p>}
    </section>
  );
}

const needsAttention = (c) => c.students > 0 && (c.started === 0 || c.on_track / c.students < 0.6);

function ClassesTab({ classes, query, setQuery, onlyProblems, setOnlyProblems }) {
  const rows = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    return (classes || [])
      .filter((c) => (!q || `${c.label} ${c.teacher}`.toLowerCase().includes(q)) && (!onlyProblems || needsAttention(c)))
      .sort((a, b) => Number(needsAttention(b)) - Number(needsAttention(a)) || a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [classes, query, onlyProblems]);
  if (!classes) return <p className="text-hv-slate text-sm">Loading…</p>;
  if (!classes.length) return <div className="rounded-2xl bg-white border border-[#E5DFC8] p-8 text-center text-hv-slate text-sm">No classes yet.</div>;
  const th = "px-4 py-3 font-bold text-left";
  const flagged = classes.filter(needsAttention).length;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="school-class-filters">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search a class or teacher" aria-label="Search classes" data-testid="school-class-search"
               className="rounded-full border-2 border-[#E5DFC8] bg-white px-4 py-1.5 text-sm w-64 focus:outline-none focus:border-hv-teal" />
        <label className="inline-flex items-center gap-2 text-sm text-hv-slate cursor-pointer">
          <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} data-testid="school-class-only-problems" />
          Only classes that need attention <span className="text-xs">({flagged})</span>
        </label>
        {(query || onlyProblems) && <button type="button" className="text-xs underline text-hv-slate" onClick={() => { setQuery(""); setOnlyProblems(false); }}>Show all {classes.length} classes</button>}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[#E5DFC8] p-8 text-center text-hv-slate text-sm">No class matches. <button type="button" className="underline" onClick={() => { setQuery(""); setOnlyProblems(false); }}>Show all classes</button></div>
      ) : (
        <div className="rounded-2xl bg-white border border-[#E5DFC8] overflow-x-auto" data-testid="class-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5DFC8] text-hv-slate">
                <th className={th}>Class · Teacher</th>
                <th className={`${th} text-right`}>Students</th>
                <th className={`${th} text-right`} title="Students who have opened at least one story">Started</th>
                <th className={th} title="Stories finished out of the stories unlocked so far">Progress</th>
                <th className={`${th} text-right`} title="Students who have finished every story unlocked so far">Up to date</th>
                <th className={`${th} text-right`} title="Students 2 or more stories behind">Need help</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.key} className="border-b border-[#E5DFC8] last:border-0" data-testid={`class-row-${c.key}`}>
                  <td className="px-4 py-3">
                    <p className="font-bold text-hv-navy">
                      {needsAttention(c) && <span className="inline-block w-2 h-2 rounded-full bg-[#E08A00] mr-2 align-middle" title="Needs attention" aria-label="Needs attention" />}
                      {c.label}
                    </p>
                    <p className="text-xs text-hv-slate">{c.teacher || "No teacher yet"}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-hv-navy">{c.students}</td>
                  <td className="px-4 py-3 text-right text-hv-navy">{c.started}</td>
                  <td className="px-4 py-3"><Bar100 value={c.avg_progress_pct} /></td>
                  <td className="px-4 py-3 text-right text-hv-navy">{c.on_track} of {c.students}</td>
                  <td className="px-4 py-3 text-right">{c.needs_help > 0 ? <span className="inline-flex rounded-full bg-[#FDECE8] px-2.5 py-0.5 text-xs font-bold text-hv-coral">{c.needs_help}</span> : <span className="text-hv-slate">0</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HelpTab({ students }) {
  if (!students) return <p className="text-hv-slate text-sm">Loading…</p>;
  if (!students.length) {
    return (
      <div className="rounded-2xl bg-white border border-[#E5DFC8] p-8 text-center" data-testid="help-empty">
        <CheckCircle2 className="w-7 h-7 mx-auto text-hv-teal" strokeWidth={2.2} />
        <p className="hv-heading text-hv-navy mt-2">Every student is up to date with their unlocked stories.</p>
      </div>
    );
  }
  const th = "px-4 py-3 font-bold text-left";
  return (
    <div className="rounded-2xl bg-white border border-[#E5DFC8] overflow-x-auto" data-testid="help-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#E5DFC8] text-hv-slate">
          <th className={th}>Student</th><th className={th}>Class</th><th className={`${th} text-right`}>Stories done</th><th className={`${th} text-right`}>To catch up</th>
        </tr></thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id} className="border-b border-[#E5DFC8] last:border-0">
              <td className="px-4 py-3 font-bold text-hv-navy">{s.name}</td>
              <td className="px-4 py-3 text-hv-navy">{s.class || "—"}</td>
              <td className="px-4 py-3 text-right text-hv-navy">{s.completed} of {s.unlocked}</td>
              <td className="px-4 py-3 text-right"><span className="inline-flex rounded-full bg-[#FDECE8] px-2.5 py-0.5 text-xs font-bold text-hv-coral">{s.behind} {s.behind === 1 ? "story" : "stories"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BrandTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white border border-[#E5DFC8] px-3 py-2 shadow-lg text-xs">
      <div className="font-bold text-hv-navy mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5 text-hv-navy">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          {p.name}: <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const axisTick = { fill: SLATE, fontSize: 11 };
const PERIODS = [{ v: "weekly", l: "Weekly" }, { v: "monthly", l: "Monthly" }];

export default function SchoolAnalyticsView({ schoolId, canDownload = true }) {
  const [snapshot, setSnapshot] = useState(null);
  const [period, setPeriod] = useState("weekly");
  const [offset, setOffset] = useState(0);
  const [periodData, setPeriodData] = useState(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  const [tab, setTab] = useState("classes");
  const [classQuery, setClassQuery] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [err, setErr] = useState("");

  const loadSnapshot = useCallback(async (id) => {
    if (!id) return;
    setLoadingSnapshot(true);
    setErr("");
    try {
      const { data } = await client.get("/school-analytics/snapshot", { params: { school_id: id } });
      setSnapshot(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoadingSnapshot(false);
    }
  }, []);

  const loadPeriod = useCallback(async (id, p, o) => {
    if (!id) return;
    setLoadingPeriod(true);
    try {
      const { data } = await client.get("/school-analytics/period", { params: { school_id: id, period: p, offset: o } });
      setPeriodData(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoadingPeriod(false);
    }
  }, []);

  useEffect(() => { if (schoolId) { setSnapshot(null); loadSnapshot(schoolId); setOffset(0); } }, [schoolId, loadSnapshot]);
  // The period numbers are only needed on their own tab, so they load when it is opened (and when the period changes).
  useEffect(() => { if (schoolId && tab === "time") loadPeriod(schoolId, period, offset); }, [schoolId, tab, period, offset, loadPeriod]);

  const chartData = useMemo(
    () => (periodData?.activity.labels || []).map((l, i) => ({ label: l, value: periodData.activity.values[i] })),
    [periodData]
  );

  const k = snapshot?.kpis;
  if (!schoolId) return null;

  const TABS = [
    { key: "classes", label: "Classes & teachers" },
    { key: "help", label: "Needs help", badge: snapshot?.attention?.length || 0 },
    { key: "time", label: "Activity over time" },
    ...(canDownload ? [{ key: "download", label: "Download report" }] : []),
  ];

  return (
    <div className="space-y-5" data-testid="school-analytics-view">
      {err && <div className="rounded-2xl bg-white border border-hv-coral/40 p-4 text-hv-coral font-bold text-sm" data-testid="school-analytics-error">{err}</div>}

      {/* the school in one line */}
      <div className={`flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl bg-white border border-[#E5DFC8] px-5 py-4 transition-opacity ${loadingSnapshot ? "opacity-50" : ""}`} data-testid="school-counts">
        <span className="inline-flex items-center gap-2 text-hv-navy"><School className="w-5 h-5 text-hv-teal" strokeWidth={2.4} /><span className="hv-heading text-lg">{snapshot?.school?.name || "…"}</span></span>
        <span className="inline-flex items-center gap-1.5 text-sm text-hv-slate" data-testid="kpi-students"><Users className="w-4 h-4 text-hv-teal" strokeWidth={2.4} /><b className="text-hv-navy">{show(k?.total_students)}</b> students</span>
        <span className="inline-flex items-center gap-1.5 text-sm text-hv-slate" data-testid="kpi-teachers"><GraduationCap className="w-4 h-4 text-hv-teal" strokeWidth={2.4} /><b className="text-hv-navy">{show(k?.teacher_count)}</b> teachers</span>
        <span className="inline-flex items-center gap-1.5 text-sm text-hv-slate" data-testid="kpi-classes"><School className="w-4 h-4 text-hv-teal" strokeWidth={2.4} /><b className="text-hv-navy">{show(k?.class_count)}</b> classes</span>
        {k && k.seats_remaining !== null && k.seats_remaining !== undefined && (
          <span className="text-sm text-hv-slate" data-testid="kpi-seats"><b className="text-hv-navy">{k.seats_remaining}</b> seats free</span>
        )}
      </div>

      {snapshot?.verdict && k?.total_students > 0 && <Verdict verdict={snapshot.verdict} />}
      {snapshot && k?.total_students === 0 && <div className="rounded-2xl bg-[#FBF8F0] border border-[#E5DFC8] px-5 py-4 text-sm text-hv-slate" data-testid="school-analytics-empty">This school has no students yet — progress appears here as soon as its teachers add their classes.</div>}

      {/* the five numbers that matter */}
      <div className={`grid grid-cols-2 lg:grid-cols-5 gap-4 transition-opacity ${loadingSnapshot ? "opacity-50" : ""}`}>
        <Tile testid="kpi-started" icon={UserCheck} value={k ? `${k.students_started} of ${k.total_students}` : "—"} label="Students who started" hint="have opened at least one story" />
        <Tile testid="kpi-stories" icon={BookOpenCheck} value={k ? `${k.stories_done_lifetime} of ${k.stories_unlocked}` : "—"} label="Stories completed" hint="out of the stories unlocked so far" />
        <Tile testid="kpi-completion" icon={Percent} value={pct(k?.completion_rate)} label="Average progress" hint="of unlocked stories, per student" />
        <Tile testid="kpi-quiz-score" icon={Trophy} value={pct(k?.avg_quiz_score)} label="Average quiz score" hint={k?.quizzes_taken ? `${k.quizzes_taken} quizzes taken` : "no quizzes taken yet"} />
        <Tile testid="kpi-attention" icon={AlertTriangle} value={show(k?.needs_attention)} label="Students who need help" hint="2 or more stories behind" />
      </div>

      {snapshot && <StatusBar status={snapshot.status} />}

      <WhereToLook data={snapshot?.attention_items} onOpen={(label) => { setTab("classes"); setOnlyProblems(false); setClassQuery(label || ""); }} />

      <details className="rounded-2xl bg-white border border-[#E5DFC8] px-5 py-3 text-sm text-hv-slate" data-testid="school-glossary">
        <summary className="cursor-pointer hv-heading text-hv-navy">How to read this page</summary>
        <dl className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          {[["Started", "the student has opened at least one story."], ["Up to date", "the student has finished every story unlocked so far for their class."],
            ["Slightly behind / Behind", "1 / 2 or more unlocked stories still to finish."], ["Not started", "nothing is unlocked for that class yet."],
            ["Progress", "stories finished out of the stories unlocked so far, not out of all 600."], ["Need help", "students 2 or more stories behind."]].map(([t, d]) => (
            <div key={t}><dt className="font-bold text-hv-navy inline">{t}: </dt><dd className="inline">{d}</dd></div>
          ))}
        </dl>
      </details>

      {/* detail, one tab at a time */}
      <div className="flex gap-2 overflow-x-auto pb-1 pt-2" role="tablist" data-testid="school-analytics-tabs">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`school-tab-${t.key}`}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition ${tab === t.key ? "bg-hv-navy border-hv-navy text-white" : "bg-white border-[#E5DFC8] text-hv-navy hover:bg-hv-cream"}`}>
            {t.label}{t.badge ? <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${tab === t.key ? "bg-white/20" : "bg-hv-cream"}`}>{t.badge}</span> : null}
          </button>
        ))}
      </div>

      {tab === "classes" && <ClassesTab classes={snapshot?.classes} query={classQuery} setQuery={setClassQuery} onlyProblems={onlyProblems} setOnlyProblems={setOnlyProblems} />}
      {tab === "help" && <HelpTab students={snapshot?.attention} />}

      {tab === "time" && (
        <div data-testid="school-period">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-hv-slate">
              {periodData?.range ? `${new Date(periodData.range.start).toLocaleDateString()} – ${new Date(periodData.range.end).toLocaleDateString()}` : "Pick a period to see what students did in it."}
            </p>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-full border-2 border-[#E5DFC8] p-1 bg-white">
                {PERIODS.map((p) => (
                  <button key={p.v} onClick={() => { setPeriod(p.v); setOffset(0); }} data-testid={`period-${p.v}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${period === p.v ? "bg-hv-navy text-white" : "text-hv-navy"}`}>
                    {p.l}
                  </button>
                ))}
              </div>
              <button onClick={() => setOffset((o) => o + 1)} data-testid="period-prev" aria-label="Previous period"
                      className="min-h-[36px] min-w-[36px] rounded-full border-2 border-[#E5DFC8] bg-white inline-flex items-center justify-center text-hv-navy hover:bg-hv-cream">
                <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
              </button>
              <button onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} data-testid="period-next" aria-label="Next period"
                      className="min-h-[36px] min-w-[36px] rounded-full border-2 border-[#E5DFC8] bg-white inline-flex items-center justify-center text-hv-navy hover:bg-hv-cream disabled:opacity-40">
                <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          <div className={`mt-4 grid sm:grid-cols-3 gap-4 transition-opacity ${loadingPeriod ? "opacity-50" : ""}`}>
            <PeriodMetricTile testid="metric-active" icon={Activity} label="Active students" delta={periodData?.summary.active_students} />
            <PeriodMetricTile testid="metric-stories" icon={BookOpenCheck} label="Stories completed in this period" delta={periodData?.summary.stories_completed} />
            <PeriodMetricTile testid="metric-quizzes" icon={Target} label="Quizzes completed in this period" delta={periodData?.summary.quizzes_completed} />
          </div>

          <div className="mt-4 rounded-2xl bg-white border border-[#E5DFC8] p-5" data-testid="activity-chart">
            <p className="hv-heading font-bold text-hv-navy">Active students each {period === "weekly" ? "day" : "week"}</p>
            <div className="mt-4" style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={BORDER} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BrandTooltip />} cursor={{ fill: "rgba(64,152,154,0.08)" }} />
                  <Bar dataKey="value" name="Active students" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={44} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === "download" && canDownload && (
        <ProgressReportPanel path="/school-analytics/report" params={{ school_id: schoolId }} fileBase={snapshot?.school?.name}
                             subtitle="the same summary and classes you see here, as a short PDF or an Excel file" studentsToggle />
      )}
    </div>
  );
}
